import React, { useState, useEffect, useMemo, useRef } from "react";
import { getBranchOwner, post, api } from "@config";
import { Modal } from "react-bootstrap";
import AIEmitButton from "../../../../../components/AIEmitButton";
import { validateCNPJ, validateCPF } from "@parts/document-component"

import Provider from "../components/provider";
import Service from "../components/service";
import RPS from "../components/rps";
import { mapTest } from "../../../../../components/pages/products/map.test";
import SweetAlert from "react-bootstrap-sweetalert";

import { useNfse } from '../context'
import {
    ActionButton,
    StyledCard,
    ScreenContainer,
    ScreenContent,
    HeroHeader,
    HeroHeaderTitle,
    HeroIcon,
    StepperWrapper,
    StepperItem,
    StepperMarker,
    FooterActions,
} from "../components/styles";
import ImportServiceWithAIModal from "../../components/ImportServiceWithAI";
import { getExigibilidadeFromNatOp } from '../utils/natOpToExigibilidade';

const PREFECTURE_ERROR_MESSAGES = {
    rps_cancelled: "A prefeitura informou que o RPS já está cancelado.",
    rps_number_used: "Essa numeração de RPS já foi utilizada.",
    rps_denied: "A prefeitura indeferiu o RPS enviado.",
    rps_duplicated: "A prefeitura identificou esse RPS como duplicado.",
    other_error: "A prefeitura rejeitou a emissão.",
};

const PREFECTURE_ERROR_HINTS = {
    rps_number_used: "Verifique o número do RPS nas configurações de NFSe e tente novamente com a próxima sequência.",
    rps_duplicated: "Confira se a nota já foi emitida anteriormente antes de reenviar.",
    rps_denied: "Revise os dados fiscais preenchidos antes de reenviar à prefeitura.",
};

const DEFAULT_PREFECTURE_ERROR_MESSAGE = "Não foi possível emitir a NFS-e. A prefeitura retornou um erro.";
const DEFAULT_INVOICY_ERROR_MESSAGE = "Não recebemos a confirmação da InvoiCy. Tente novamente em instantes.";
const DEFAULT_INVOICY_ERROR_HINT = "Se o erro persistir, confira se a empresa está corretamente cadastrada na InvoiCy ou contacte o suporte.";

function tryParseJsonString(value) {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) {
        return null;
    }
    try {
        return JSON.parse(trimmed);
    } catch (err) {
        return null;
    }
}

function coerceToObject(value) {
    if (!value) {
        return null;
    }
    if (typeof value === "object") {
        return value;
    }
    const direct = tryParseJsonString(value);
    if (direct) {
        return direct;
    }
    if (typeof value === "string") {
        const start = value.indexOf("{");
        const end = value.lastIndexOf("}");
        if (start !== -1 && end !== -1 && end > start) {
            const candidate = value.slice(start, end + 1);
            return tryParseJsonString(candidate);
        }
    }
    return null;
}

function flattenDetails(value) {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map((item) => String(item)).filter(Boolean);
    }
    if (typeof value === "object") {
        return Object.values(value)
            .reduce((acc, item) => acc.concat(flattenDetails(item)), [])
            .filter(Boolean);
    }
    if (typeof value === "string" || typeof value === "number") {
        return [String(value)];
    }
    return [];
}

function parsePrefectureErrorPayload(error) {
    if (!error) {
        return null;
    }

    if (typeof error === "string") {
        return tryParseJsonString(error) || error;
    }

    if (typeof error === "object") {
        if (error.response) {
                const responseData = error.response.data || error.response;
                const parsed = typeof responseData === "string" ? tryParseJsonString(responseData) : null;
                return parsed || responseData;
        }
        if (error.data) {
            const parsed = typeof error.data === "string" ? tryParseJsonString(error.data) : null;
            return parsed || error.data;
        }
        return error;
    }

    return null;
}

function normalizePrefectureError(error) {
    const fallback = {
        show: true,
        description: DEFAULT_PREFECTURE_ERROR_MESSAGE,
        code: error && error.status ? error.status : null,
        tag: null,
        details: [],
    };

    let payload = parsePrefectureErrorPayload(error);
    if (!payload) {
        return fallback;
    }

    if (typeof payload === "string") {
        const parsed = coerceToObject(payload);
        if (parsed) {
            payload = parsed;
        } else {
            return {
                ...fallback,
                description: payload || fallback.description,
            };
        }
    }

    let data = payload.error || payload.data || payload;
    if (!data || typeof data !== "object") {
        const nestedObject = coerceToObject(data);
        if (nestedObject && typeof nestedObject === "object") {
            data = nestedObject;
        } else {
            return fallback;
        }
    }

    let tag = data.tag || null;
    let code = data.code || fallback.code;

    let baseDescription = data.description || data.detail;
    const nestedDescriptionObject = typeof baseDescription === "string" ? coerceToObject(baseDescription) : null;
    if (nestedDescriptionObject && typeof nestedDescriptionObject === "object") {
        baseDescription = nestedDescriptionObject.description || nestedDescriptionObject.detail || baseDescription;
        tag = tag || nestedDescriptionObject.tag || null;
        code = nestedDescriptionObject.code || code;
        data = {
            ...data,
            details: data.details || nestedDescriptionObject.details || [],
            errors: data.errors || nestedDescriptionObject.errors || [],
        };
        data._nested = nestedDescriptionObject;
    }

    const isGenericInvoicyError = !tag && !PREFECTURE_ERROR_MESSAGES[code] && !baseDescription;
    const friendly = tag && PREFECTURE_ERROR_MESSAGES[tag] ? PREFECTURE_ERROR_MESSAGES[tag] : null;
    const isUnexpectedInvoicyResponse = typeof baseDescription === "string" && baseDescription.toLowerCase().includes("resposta inesperada da invoicy");
    const rawDescription = baseDescription || friendly || (isGenericInvoicyError ? DEFAULT_INVOICY_ERROR_MESSAGE : DEFAULT_PREFECTURE_ERROR_MESSAGE);
    let description = isUnexpectedInvoicyResponse ? DEFAULT_INVOICY_ERROR_MESSAGE : rawDescription;

    const details = new Set();
    if (friendly && friendly !== description) {
        details.add(friendly);
    }

    const detailSources = [data];
    if (data._nested) {
        detailSources.push(data._nested);
    }

    detailSources.forEach((source) => {
        ["detail", "details", "errors", "non_field_errors"].forEach((field) => {
            flattenDetails(source && source[field]).forEach((item) => details.add(item));
        });
    });

    if (typeof rawDescription === "string") {
        const codeMatches = rawDescription.match(/Código:\s*[^,]+,\s*Mensagem:[^\.]+(?:\.|$)/g);
        if (codeMatches && codeMatches.length) {
            codeMatches.forEach((match) => {
                const cleaned = match.replace(/\s+/g, " ").trim();
                if (cleaned) {
                    details.add(cleaned);
                }
            });
            const firstMatchIndex = rawDescription.indexOf(codeMatches[0]);
            if (firstMatchIndex > 0) {
                const summary = rawDescription.slice(0, firstMatchIndex).trim();
                if (summary) {
                    description = summary;
                }
            }
        }
        const correctionMatches = rawDescription.match(/Correção:[^\.]+/g);
        if (correctionMatches) {
            correctionMatches.forEach((item) => details.add(item.trim()));
        }
    }

    const hint = tag && PREFECTURE_ERROR_HINTS[tag];
    if (hint) {
        details.add(hint);
    }
    if (isUnexpectedInvoicyResponse) {
        const cleaned = baseDescription.replace(/.*Resposta inesperada da InvoiCy:/i, "").trim();
        if (cleaned) {
            details.add(cleaned.replace(/[{}]/g, '').trim());
        }
        details.add(DEFAULT_INVOICY_ERROR_HINT);
    }
    if (isGenericInvoicyError) {
        details.add(DEFAULT_INVOICY_ERROR_HINT);
    }

    return {
        show: true,
        description,
        code,
        tag,
        details: Array.from(details).filter(Boolean),
    };
}

function SwitchComponent(props) {
    const { step } = props;
    switch (step) {
        case 0:
            return <Provider />;
        case 1:
            return <Service />;
        default:
            return null;
    }
}

export default function NfPaulistanaPattern() {
    const { rps, taker, service, setRps, setTaker, setService, setItems, clearDraft } = useNfse();
    const [step, setStep] = useState(0);
    const hasDraftData = useMemo(() => {
        const hasRps = rps && Object.keys(rps).length;
        const hasTaker = taker && Object.keys(taker).length;
        const hasService = service && Object.keys(service).length;
        return Boolean(hasRps || hasTaker || hasService);
    }, [rps, taker, service]);

    useEffect(() => {
        const duplicateData = localStorage.getItem('nfseDuplicateData');
        if (duplicateData) {
            try {
                const data = JSON.parse(duplicateData);
                if (data.rps) setRps(data.rps);
                if (data.taker) setTaker(data.taker);
                if (data.service) setService(data.service);
                if (data.items) setItems(data.items);
                localStorage.removeItem('nfseDuplicateData');
            } catch(e) {
                console.error("Error parsing duplicate data", e);
            }
        }
    }, [setRps, setTaker, setService, setItems]);
    const takerRequired = ["cpf_cnpj", "state", "city"];
    const [modalError, setModalError] = useState({ show: false, description: "", code: null, tag: null, details: [], rawResponse: "" });
    const [modalValidation, setModalValidation] = useState({ show: false, listErrors: [] });
    const [modalSending, setModalSending] = useState(false);
    // Guard síncrono contra duplo clique/duplo submit — setState é assíncrono
    // e não bloqueia uma segunda chamada de onEmit() disparada antes do re-render.
    const isEmittingRef = useRef(false);
    const [nfseModalSuccess, setNfseModalSuccess] = useState(false);
    const [nfseData, setNfseData] = useState(null);
    const [showAIModal, setShowAIModal] = useState(false);

    const handleApplyAIData = (data, options) => {
        if (!data) return;

        const mergedTaker = Object.assign({}, taker, data.taker || {});
        const mergedService = Object.assign({}, service, data.service || {});

        if (data.taker) {
            setTaker(prev => ({
                ...prev,
                ...data.taker
            }));
        }
        if (data.service) {
            setService(prev => ({
                ...prev,
                ...data.service
            }));
        }
        if (data.items && data.items.length > 0) {
            setItems(data.items);
        }

        if (options && options.autoEmit) {
            onEmit({
                taker: mergedTaker,
                service: mergedService,
                rps: rps
            });
        }
    };

    const removeTakerErrors = () => {
        // No longer used, handled by Modal
    };

    const handleClearDraft = () => {
        clearDraft();
        setStep(0);
    };

    const validateTakerData = (targetTaker) => {
        const currentTaker = targetTaker || taker;
        let listErrors = [];
        const required = ["cpf_cnpj", "name", "postal_code", "address", "city", "state", "district", "street_number"];
        const fieldNames = {
            "cpf_cnpj": "CPF/CNPJ",
            "name": "Razão Social/Nome",
            "postal_code": "CEP",
            "address": "Endereço",
            "city": "Cidade",
            "state": "Estado",
            "district": "Bairro",
            "street_number": "Número"
        };

        if (!currentTaker || !currentTaker.original_id) {
            listErrors.push("O Tomador precisa estar cadastrado como cliente na base de dados para emitir a NFS-e. Clique em 'Cadastrar como Cliente' na seção do Tomador.");
        }

        for (let field of required) {
            let fieldValue = currentTaker ? currentTaker[field] : null;

            if (field === "cpf_cnpj") {
                let doc = !fieldValue ? "" : String(fieldValue).replace(/\D+/g, "");
                if (!doc) {
                    listErrors.push("Insira um CPF/CNPJ válido do Tomador");
                } else if (doc.length <= 11 && !validateCPF(doc)) {
                    listErrors.push("Insira um CPF válido para o Tomador");
                } else if (doc.length > 11 && !validateCNPJ(doc)) {
                    listErrors.push("Insira um CNPJ válido para o Tomador");
                }
            } else if (!fieldValue || String(fieldValue).trim() === "") {
                listErrors.push(`O campo ${fieldNames[field]} do Tomador é obrigatório`);
            }
        }

        if (listErrors.length === 0) {
            return true;
        } else {
            setModalValidation({ show: true, listErrors });
            return false;
        }
    };

    const validateServiceData = (targetService) => {
        const currentService = targetService || service;
        let listErrors = [];

        if (!currentService || !currentService.nat_op) listErrors.push("Natureza da Operação do Serviço é obrigatória");
        if (!currentService || !currentService.cnae_code) listErrors.push("Atividade (CNAE) do Serviço é obrigatória");
        if (!currentService || !currentService.item_list_service) listErrors.push("Item Lei do Serviço é obrigatório");
        if (!currentService || !currentService.service_code) listErrors.push("Código do Serviço é obrigatório");
        if (!currentService || !currentService.description || String(currentService.description).trim() === "") listErrors.push("Descrição da Nota é obrigatória");
        const branch = getBranchOwner();
        const cityCodeProvider = (currentService && currentService.city_code_provider) || (branch && branch.address && branch.address.city_code) || "";
        if (!cityCodeProvider || String(cityCodeProvider).trim() === "") {
            listErrors.push("Município da Prestação do Serviço (Cód. IBGE) é obrigatório");
        }

        if (listErrors.length === 0) {
            return true;
        } else {
            setModalValidation({ show: true, listErrors });
            return false;
        }
    };

    const closePrefectureErrorModal = () => {
        setModalError({ show: false, description: "", code: null, tag: null, details: [], rawResponse: "" });
    };

    const onEmit = (customData) => {
        // Bloqueia cliques/chamadas concorrentes: sem isso, dois cliques rápidos no
        // botão de emissão disparam 2 POSTs de criação de NFS-e antes do primeiro
        // re-render (setModalSending é assíncrono), gerando envio duplicado do
        // mesmo RPS para a prefeitura.
        if (isEmittingRef.current) {
            return;
        }

        const currentTaker = (customData && customData.taker) ? customData.taker : taker;
        const currentService = (customData && customData.service) ? customData.service : service;
        const currentRps = (customData && customData.rps) ? customData.rps : rps;

        const takerValid = validateTakerData(currentTaker);
        if (!takerValid) return;

        const serviceValid = validateServiceData(currentService);
        if (!serviceValid) {
            setStep(1);
            return;
        }

        isEmittingRef.current = true;

        const branch = getBranchOwner();
        currentRps.rps_nat_op = (currentService.nat_op && currentService.nat_op.value) || currentRps.rps_nat_op || "1";

        const data = {
            rps: currentRps,
            taker: currentTaker,
            service: Object.assign({}, currentService)
        };
        data.service.quantity = 1;

        const natOpValue = (currentService.nat_op && currentService.nat_op.value) || currentRps.rps_nat_op || "1";
        data.service.exigibilidade_iss = getExigibilidadeFromNatOp(natOpValue);
        data.service.iss_retained = data.service.iss_retained || "2";
        data.service.city_code_provider = data.service.city_code_provider || (branch && branch.address && branch.address.city_code) || "";

        if (data.taker && data.taker.cpf_cnpj) {
            data.taker.cpf_cnpj = String(data.taker.cpf_cnpj).replace(/\D+/g, "");
        }

        setModalSending(true);
        const url = `${api.nfse.nfse}?branch_owner=${branch.id}`;

        const onSuccess = (nfse) => {
            isEmittingRef.current = false;
            setModalSending(false);
            clearDraft();
            setNfseData(nfse);
            setNfseModalSuccess(true);
        };

        const errorCallback = (error) => {
            let rawText = "";
            try {
                if (error && typeof error.response === "string") rawText = error.response;
                else if (error && error.responseText) rawText = error.responseText;
                else if (error && error.data && typeof error.data === "string") rawText = error.data;
                else rawText = JSON.stringify(error);
            } catch(e) { rawText = String(error); }
            isEmittingRef.current = false;
            setModalSending(false);
            const normalized = normalizePrefectureError(error);
            normalized.rawResponse = rawText;
            if (normalized.details.length === 0 && rawText) {
                normalized.details = ["Resposta bruta: " + rawText];
            }
            setModalError(normalized);
        };

        post(url, data, onSuccess, errorCallback);
    };

    const changeStep = (e) => {
        let i = parseInt(e.target.value || e.target.getAttribute('value') || e.currentTarget.value);

        if (step === 0) {
            const next = validateTakerData();
            if (!next && i > 0) {
                return;
            }
        } else if (step === 1 && i > 0) {
            const serviceNext = validateServiceData();
            if (!serviceNext) {
                return;
            }
            onEmit();
            return;
        }

        setStep(prev => prev + i);
    };

    let nextStepTextButton = "";
    let prevStepTextButton = "";

    switch (step) {
        case 0:
            nextStepTextButton = "Serviço";
            break;
        case 1:
            prevStepTextButton = "Dados do Tomador";
            nextStepTextButton = "Emitir";
            break;
        default:
            break;
    }

    return (
        <ScreenContainer>
            <ScreenContent>
                <HeroHeader>
                    <HeroHeaderTitle>
                        <HeroIcon>
                            <i className="fas fa-file-invoice-dollar"></i>
                        </HeroIcon>
                        <div>
                            <h1 className="m-0" style={{ fontWeight: 800, fontSize: '1.75rem', color: '#101828' }}>Emitir NFS-e</h1>
                            <span style={{ color: '#475467', fontSize: '0.95rem', fontWeight: 500 }}>Preencha os dados em etapas para emissão da nota fiscal</span>
                        </div>
                    </HeroHeaderTitle>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <AIEmitButton onClick={() => setShowAIModal(true)} />
                        <ActionButton
                            variant="secondary"
                            onClick={handleClearDraft}
                            disabled={!hasDraftData}
                            $stackedOnMobile
                        >
                            <i className="fas fa-eraser" style={{ marginRight: '8px' }}></i>
                            Limpar rascunho
                        </ActionButton>
                    </div>
                </HeroHeader>

                <StyledCard style={{ background: '#fff' }}>
                    <StepperWrapper>
                        <StepperItem active={step >= 0}>
                            <StepperMarker active={step >= 0}>1</StepperMarker>
                            <strong style={{ color: step >= 0 ? '#0088cc' : '#667085' }}>Prestador e Tomador</strong>
                        </StepperItem>
                        <StepperItem active={step >= 1}>
                            <StepperMarker active={step >= 1}>2</StepperMarker>
                            <strong style={{ color: step >= 1 ? '#0088cc' : '#667085' }}>Serviço e RPS</strong>
                        </StepperItem>
                    </StepperWrapper>
                </StyledCard>

                <RPS />

                <SwitchComponent step={step} />

                <FooterActions>
                    {step > 0 ? (
                        <ActionButton
                            variant="secondary"
                            value={-1}
                            onClick={changeStep}
                            $stackedOnMobile
                            style={{ background: '#fff', flex: 1 }}
                        >
                            <i className="fas fa-arrow-left"></i> Voltar ({prevStepTextButton})
                        </ActionButton>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}
                    <ActionButton
                        id={mapTest.emitNfse.nextStepButton}
                        variant="primary"
                        value={1}
                        onClick={changeStep}
                        disabled={modalSending}
                        $stackedOnMobile
                        style={{ flex: 1 }}
                    >
                        Próximo: {nextStepTextButton} <i className="fas fa-arrow-right"></i>
                    </ActionButton>
                </FooterActions>

                <Modal show={modalSending} centered backdrop="static">
                    <Modal.Body style={{ padding: '32px', textAlign: 'center' }}>
                        <i className="fas fa-circle-notch fa-spin text-info mb-3" style={{ fontSize: '3rem' }}></i>
                        <h4 style={{ fontWeight: 700 }}>Transmitindo NFS-e...</h4>
                        <p className="text-secondary m-0">Por favor, aguarde a resposta da prefeitura.</p>
                    </Modal.Body>
                </Modal>

                <SweetAlert
                    show={modalError.show}
                    showCancel={false}
                    showConfirm={false}
                    type='warning'
                    title={<div style={{ fontWeight: 800 }}>Erro na emissão!</div>} >
                    <div style={{ textAlign: 'left', color: '#475467' }}>
                        <p style={{ fontSize: '1.05rem', margin: '12px 0' }}>
                            {modalError.description || DEFAULT_PREFECTURE_ERROR_MESSAGE}
                        </p>
                        {modalError.code ? (
                            <p style={{ marginBottom: '4px', fontSize: '0.95rem' }}>
                                <strong>Código:</strong> {modalError.code}
                            </p>
                        ) : null}
                        {modalError.tag ? (
                            <p style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                                <strong>Motivo:</strong> {modalError.tag}
                            </p>
                        ) : null}
                        {modalError.details && modalError.details.length ? (
                            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                {modalError.details.map((detail, idx) => (
                                    <li key={idx} style={{ fontSize: '0.9rem' }}>{detail}</li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                    <ActionButton variant="secondary" style={{ width: '100%' }} onClick={closePrefectureErrorModal}>
                        Entendi e Fechar
                    </ActionButton>
                </SweetAlert>

                <SweetAlert
                    show={modalValidation.show}
                    showCancel={false}
                    showConfirm={false}
                    type='error'
                    title={<div style={{ fontWeight: 800 }}>Verifique os dados!</div>} >
                    <ul style={{ textAlign: 'left', margin: '16px 0', paddingLeft: '24px' }}>
                        {modalValidation.listErrors && modalValidation.listErrors.map((e, idx) => <li key={idx} style={{ color: '#475467', marginBottom: '8px' }}>{e}</li>)}
                    </ul>
                    <ActionButton variant="secondary" style={{ width: '100%' }} onClick={() => setModalValidation({ show: false, listErrors: [] })}>
                        Ok, entendi
                    </ActionButton>
                </SweetAlert>

                <Modal show={nfseModalSuccess} onHide={() => window.location.reload()} centered>
                    <Modal.Body style={{ padding: '32px', textAlign: 'center' }}>
                        <div style={{ width: '64px', height: '64px', background: '#D1FADF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                            <i className="fas fa-check text-success" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h2 style={{ fontWeight: 800, color: '#101828' }}>NFS-e Autorizada com Sucesso!</h2>
                        <p className="text-secondary mb-4">A nota fiscal foi processada pela prefeitura e está pronta para uso.</p>

                        {nfseData && (
                            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                                <ActionButton variant="secondary" onClick={() => window.open(nfseData.pdf_url, "_blank")}>
                                    <i className="fas fa-file-pdf text-danger"></i> Imprimir PDF
                                </ActionButton>
                                <ActionButton variant="secondary" onClick={() => window.open(nfseData.xml_url, "_blank")}>
                                    <i className="fas fa-file-code text-info"></i> Baixar XML
                                </ActionButton>
                            </div>
                        )}

                        <ActionButton variant="primary" style={{ width: '100%', marginTop: '24px' }} onClick={() => window.location.reload()}>
                            Emitir Nova Nota
                        </ActionButton>
                    </Modal.Body>
                </Modal>
                <ImportServiceWithAIModal
                    show={showAIModal}
                    onHide={() => setShowAIModal(false)}
                    onApplyData={handleApplyAIData}
                />
            </ScreenContent>
        </ScreenContainer>
    );
}



// WEBPACK FOOTER //
// ./src/views/Fiscal/NFSe/emit/patterns/index.jsx